<template>
  <div class="volunteer-dashboard">
    <!-- Hero Header -->
    <div class="hero-header">
      <v-container fluid class="pa-0">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">
              <v-icon large class="mr-3 heart-icon">mdi-heart-multiple</v-icon>
              Volunteer Rewards Center
            </h1>
            <p class="hero-subtitle">Empowering communities through meaningful recognition</p>
          </div>
          <div class="hero-decoration">
            <div class="floating-hearts">
              <v-icon class="heart-float heart-1">mdi-heart</v-icon>
              <v-icon class="heart-float heart-2">mdi-hand-heart</v-icon>
              <v-icon class="heart-float heart-3">mdi-account-heart</v-icon>
            </div>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="dashboard-content">
      <!-- Stats Cards with Volunteer Theme -->
      <v-row class="mb-8">
        <v-col cols="12" sm="6" md="3">
          <v-card
            class="volunteer-stat-card active-rewards-card elevation-8"
            @click="fetchActiveCoupons"
            :class="{ 'active-filter': currentFilter === 'active' }"
          >
            <div class="card-glow"></div>
            <v-card-text class="d-flex align-center pa-6">
              <div class="stat-icon-wrapper active-icon">
                <v-icon large class="stat-icon">mdi-gift-open</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-title">Active Rewards</div>
                <div class="stat-value">{{ activeCount }}</div>
                <div class="stat-description">Ready to inspire</div>
              </div>
            </v-card-text>
            <div class="card-pattern"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="volunteer-stat-card expired-rewards-card elevation-8"
            @click="fetchExpiredCoupons"
            :class="{ 'active-filter': currentFilter === 'expired' }"
          >
            <div class="card-glow"></div>
            <v-card-text class="d-flex align-center pa-6">
              <div class="stat-icon-wrapper expired-icon">
                <v-icon large class="stat-icon">mdi-clock-alert</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-title">Past Rewards</div>
                <div class="stat-value">{{ expiredCount }}</div>
                <div class="stat-description">Memories made</div>
              </div>
            </v-card-text>
            <div class="card-pattern"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="volunteer-stat-card total-rewards-card elevation-8"
            @click="currentFilter = 'all'"
          >
            <div class="card-glow"></div>
            <v-card-text class="d-flex align-center pa-6">
              <div class="stat-icon-wrapper total-icon">
                <v-icon large class="stat-icon">mdi-trophy-award</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-title">Total Impact</div>
                <div class="stat-value">{{ activeCount + expiredCount }}</div>
                <div class="stat-description">Lives touched</div>
              </div>
            </v-card-text>
            <div class="card-pattern"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card
            class="volunteer-stat-card create-reward-card elevation-8"
            @click="openCreateDialog"
          >
            <div class="card-glow"></div>
            <v-card-text class="d-flex align-center pa-6">
              <div class="stat-icon-wrapper create-icon">
                <v-icon large class="stat-icon">mdi-plus-circle</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-title">Create New</div>
                <div class="stat-value">
                  <v-btn
                    class="create-btn"
                    color="transparent"
                    elevation="0"
                    small
                  >
                    <v-icon>mdi-hand-heart</v-icon>
                  </v-btn>
                </div>
                <div class="stat-description">Spread kindness</div>
              </div>
            </v-card-text>
            <div class="card-pattern"></div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Data Table -->
      <v-card class="volunteer-table-card elevation-12">
        <div class="table-header">
          <div class="table-header-content">
            <div class="table-title-section">
              <v-icon large class="mr-3 table-icon">mdi-clipboard-list</v-icon>
              <div>
                <h2 class="table-title">Community Rewards</h2>
                <p class="table-subtitle">Managing appreciation for our amazing volunteers</p>
              </div>
            </div>
            <div class="table-actions">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Search rewards..."
                single-line
                hide-details
                outlined
                dense
                class="search-field"
                color="primary"
              ></v-text-field>
            </div>
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :search="search"
          :items-per-page="5"
          class="volunteer-data-table"
          mobile-breakpoint="0"
        >
          <template v-slot:[`item.sno`]="{ index }">
            <div class="row-number">
              <span class="number-badge">{{ index + 1 }}</span>
            </div>
          </template>

          <template v-slot:[`item.title`]="{ item }">
            <div class="reward-title-cell">
              <v-avatar class="reward-avatar" size="40">
                <v-icon class="avatar-icon">mdi-gift</v-icon>
              </v-avatar>
              <div class="reward-title-text">
                <div class="reward-name">{{ item.title }}</div>
                <div class="reward-type">Community Reward</div>
              </div>
            </div>
          </template>

          <template v-slot:[`item.couponCode`]="{ item }">
            <v-chip
              class="coupon-code-chip"
              color="primary"
              text-color="white"
              small
            >
              <v-icon left small>mdi-ticket</v-icon>
              {{ item.couponCode }}
            </v-chip>
          </template>

          <template v-slot:[`item.formattedExpiryDate`]="{ item }">
            <v-chip
              class="expiry-chip"
              :class="isExpired(item.expiryDate) ? 'expired-chip' : 'active-chip'"
              small
            >
              <v-icon left small>
                {{ isExpired(item.expiryDate) ? 'mdi-clock-alert' : 'mdi-clock-check' }}
              </v-icon>
              {{ formatDate(item.expiryDate) }}
            </v-chip>
          </template>

          <template v-slot:[`item.redeemedUsersCount`]="{ item }">
            <div class="redeemed-cell">
              <v-chip
                :color="item.redeemedUsersCount > 0 ? 'success' : 'grey'"
                :text-color="item.redeemedUsersCount > 0 ? 'white' : 'white'"
                small
              >
                <v-icon left small>mdi-account-check</v-icon>
                {{ item.redeemedUsersCount }}
              </v-chip>
            </div>
          </template>

          <template v-slot:[`item.count`]="{ item }">
            <div class="availability-cell">
              <div class="availability-bar">
                <v-progress-linear
                  :value="(item.redeemedUsersCount / (item.count + item.redeemedUsersCount)) * 100"
                  height="8"
                  color="success"
                  background-color="grey lighten-3"
                  rounded
                ></v-progress-linear>
              </div>
              <div class="availability-text">
                <span class="available-count">{{ item.count }}</span> available
              </div>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-tooltip top v-if="item.redeemedUsersCount > 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="action-btn view-btn"
                    @click.stop="viewItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>View Redemptions</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="action-btn edit-btn"
                    @click.stop="editItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Edit Reward</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="action-btn delete-btn"
                    @click.stop="deleteItem(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Remove Reward</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="no-data-state">
              <div class="no-data-icon">
                <v-icon size="80" color="grey lighten-2">mdi-heart-broken</v-icon>
              </div>
              <div class="no-data-title">No rewards found</div>
              <div class="no-data-subtitle">Start creating meaningful rewards for your volunteers</div>
              <v-btn
                class="no-data-btn"
                color="primary"
                large
                @click="openCreateDialog"
              >
                <v-icon left>mdi-plus</v-icon>
                Create First Reward
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Dialogs -->
    <create-awards
      :dialog="createDialog"
      :onClose="onCloseDialog"
      v-if="createDialog"
    />
    <edit-awards
      :dialog="editDialog"
      :onClose="onCloseDialog"
      :item="editedItem"
      v-if="editDialog"
    />
    <confirm-delete-dialog
      :deleteDialog="deleteDialog"
      :onClose="onCloseDialog"
      :onConfirm="onDeleteAward"
    />
    <coupons-view-dialog
      :viewDialog="viewDialog"
      :onClose="onCloseDialog"
      :item="viewedItem"
      :itemId="viewedItem._id"
      v-if="viewDialog"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CreateAwards from "../../awards/CreateAwards.vue";
import EditAwards from "../../awards/EditAwards.vue";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import CouponsViewDialog from "../../awards/CouponsViewDialog.vue";

export default {
  name: "VolunteerDashboard",
  components: {
    CreateAwards,
    EditAwards,
    CouponsViewDialog,
    ConfirmDeleteDialog
  },
  data() {
    return {
      headers: [
        { text: "#", value: "sno", width: "5%", sortable: false },
        { text: "Reward Title", value: "title", width: "25%" },
        { text: "Code", value: "couponCode", width: "15%" },
        { text: "Goal Hours", value: "goal_hours", width: "15%" },
        { text: "Availability", value: "count", width: "15%" },
        { text: "Valid Until", value: "formattedExpiryDate", width: "15%" },
        { text: "Redeemed", value: "redeemedUsersCount", width: "10%" },
        { text: "Actions", value: "actions", align: "end", width: "15%", sortable: false }
      ],
      search: "",
      createDialog: false,
      editDialog: false,
      viewDialog: false,
      deleteDialog: false,
      editedItem: {},
      viewedItem: {},
      award: null,
      currentFilter: "all",
      activeCount: 0,
      expiredCount: 0
    };
  },
  computed: {
    ...mapGetters("awards", ["getAllAwards"]),
    filteredItems() {
      return this.getAllAwards
        .filter(
          item =>
            this.currentFilter === "all" ||
            (this.currentFilter === "active" && !this.isExpired(item.expiryDate)) ||
            (this.currentFilter === "expired" && this.isExpired(item.expiryDate))
        )
        .map(item => ({
          ...item,
          formattedExpiryDate: this.formatDate(item.expiryDate)
        }));
    }
  },
  methods: {
    ...mapActions("awards", [
      "fetchAllAwards",
      "getCopuonCounts",
      "deleteAward"
    ]),
    isExpired(date) {
      return new Date(date) < new Date();
    },
    async fetchActiveCoupons() {
      this.currentFilter = "active";
      await this.fetchAllAwards({ type: "active" });
    },
    async fetchExpiredCoupons() {
      this.currentFilter = "expired";
      await this.fetchAllAwards({ type: "expired" });
    },
    openCreateDialog() {
      this.createDialog = true;
    },
    onCloseDialog() {
      if (!this.createDialog || !this.editDialog) {
        this.loadCouponCounts();
      }
      this.createDialog = false;
      this.editDialog = false;
      this.deleteDialog = false;
      this.viewDialog = false;
    },
    editItem(item) {
      this.editedItem = { ...item };
      this.editDialog = true;
    },
    viewItem(item) {
      this.viewedItem = { ...item };
      this.viewDialog = true;
    },
    formatDate(date) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(date).toLocaleDateString(undefined, options);
    },
    deleteItem(item) {
      this.award = item;
      this.deleteDialog = true;
    },
    async onDeleteAward() {
      await this.deleteAward({ _id: this.award._id });
      this.deleteDialog = false;
      this.loadCouponCounts();
    },
    async loadCouponCounts() {
      try {
        const counts = await this.getCopuonCounts();
        this.activeCount = counts.activeCount || 0;
        this.expiredCount = counts.inactiveCount || 0;
      } catch (error) {
        console.error("Failed to load coupon counts:", error);
      }
    }
  },
  async created() {
    await this.fetchAllAwards();
    await this.loadCouponCounts();
  }
};
</script>

<style scoped lang="scss">
.volunteer-dashboard {
  // background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  position: relative;
}

// Hero Header
.hero-header {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 2rem 0;
  margin-bottom: 2rem;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.hero-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-title {
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-weight: 400;
}

.heart-icon {
  color: #ff6b6b !important;
  animation: heartbeat 2s infinite;
}

@keyframes heartbeat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

// Floating Hearts Animation
.floating-hearts {
  position: relative;
  width: 100px;
  height: 100px;
}

.heart-float {
  position: absolute;
  color: rgba(255, 255, 255, 0.3);
  animation: float 6s ease-in-out infinite;
}

.heart-1 {
  top: 0;
  left: 0;
  animation-delay: 0s;
}

.heart-2 {
  top: 20px;
  right: 0;
  animation-delay: 2s;
}

.heart-3 {
  bottom: 0;
  left: 50%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.3; }
  50% { transform: translateY(-20px) rotate(10deg); opacity: 0.8; }
}

// Dashboard Content
.dashboard-content {
  position: relative;
  z-index: 1;
}

// Volunteer Stat Cards
.volunteer-stat-card {
  position: relative;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  overflow: hidden;

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  &.active-filter {
    border: 2px solid #4CAF50;
    background: rgba(76, 175, 80, 0.1);
  }
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.volunteer-stat-card:hover .card-glow {
  opacity: 1;
}

// Individual Card Themes
.active-rewards-card {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  // background: linear-gradient(90deg, #4f46e5, #10b981)
}

.expired-rewards-card {
  // background: linear-gradient(135deg, #FF7043, #F4511E);
  background: linear-gradient(135deg, #e5741ede, #e5741ede);

}

.total-rewards-card {
  background: linear-gradient(135deg, #42A5F5, #1E88E5);
  // background: linear-gradient(90deg, #4f46e5, #10b981)

}

.create-reward-card {
  background: linear-gradient(135deg, #AB47BC, #8E24AA);
  // background: linear-gradient(90deg, #4f46e5, #10b981)

}

.stat-icon-wrapper {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.stat-icon {
  color: white !important;
  font-size: 2rem !important;
}

.stat-content {
  flex: 1;
}

.stat-title {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.stat-value {
  color: white;
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  font-style: italic;
}

.create-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border-radius: 50% !important;
  width: 40px !important;
  height: 40px !important;
}

// Table Card
.volunteer-table-card {
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  // border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
}

.table-header {
  background: linear-gradient(135deg,#084468,#0a5b8a);
  color: white;
  padding: 2rem;
}

.table-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.table-title-section {
  display: flex;
  align-items: center;
}

.table-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

.table-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.table-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
}

.search-field {
  max-width: 300px;
  border-radius: 20px;
  ::v-deep .v-input__slot {
    background: rgba(255, 255, 255, 0.15) !important;
    // border-color: rgba(255, 255, 255, 0.3) !important;

    .v-text-field__slot input {
      color: white !important;
    }

    .v-label {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    .v-icon {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }
}

// Data Table Styling
.volunteer-data-table {
  background: #fff;

  ::v-deep .v-data-table-header {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);

    th {
      font-weight: 700 !important;
      color: #495057 !important;
      font-size: 0.95rem !important;
      border-bottom: 2px solid #dee2e6 !important;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05)) !important;
      transform: scale(1.01);
    }

    td {
      padding: 1rem 16px !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    }
  }
}

// Table Cell Styling
.row-number {
  .number-badge {
    // background: linear-gradient(135deg, #667eea, #764ba2);
    color: black;
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
  }
}

.reward-title-cell {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.reward-avatar {
  background: linear-gradient(135deg, #4CAF50, #45a049) !important;

  .avatar-icon {
    color: white !important;
  }
}

.reward-title-text {
  .reward-name {
    font-weight: 600;
    color: #2d3748;
    font-size: 1rem;
  }

  .reward-type {
    color: #718096;
    font-size: 0.875rem;
  }
}

.coupon-code-chip {
  font-weight: 600 !important;
  background: linear-gradient(135deg, #084468, #0978b8) !important;
}

.expiry-chip {
  font-weight: 500;

  &.active-chip {
    background: linear-gradient(135deg, #4CAF50, #46b74b) !important;
    color: white !important;
  }

  &.expired-chip {
    background: linear-gradient(135deg, #f5673c, #f467208a) !important;
    color: white !important;
  }
}

.availability-cell {
  .availability-bar {
    margin-bottom: 0.5rem;
  }

  .availability-text {
    font-size: 0.875rem;
    color: #718096;

    .available-count {
      font-weight: 600;
      color: #4CAF50;
    }
  }
}

.redeemed-cell {
  .v-chip {
    font-weight: 600;
  }
}

// Action Buttons
.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn {
  width: 36px !important;
  height: 36px !important;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  &.view-btn {
    // background: linear-gradient(135deg, #42A5F5, #1E88E5) !important;
    color: #42A5F5 !important;
  }

  &.edit-btn {
    // background: linear-gradient(135deg, #FFA726, #FF9800) !important;
    color: #FFA726 !important;
  }

  &.delete-btn {
    // background: linear-gradient(135deg, #EF5350, #E53935) !important;
    color: #EF5350 !important;
  }
}

// No Data State
.no-data-state {
  text-align: center;
  padding: 4rem 2rem;

  .no-data-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #4a5568;
    margin: 1rem 0 0.5rem 0;
  }

  .no-data-subtitle {
    color: #718096;
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  .no-data-btn {
    background: linear-gradient(135deg,#084468,#0a5b8a) !important;
    color: white !important;
    border-radius: 25px !important;
    padding: 0 2rem !important;
    font-weight: 600 !important;
  }
}

// Responsive Design
@media (max-width: 960px) {
  .hero-title {
    font-size: 2rem;
  }

  .table-header-content {
    flex-direction: column;
    gap: 1rem;
  }

  .search-field {
    max-width: 100%;
  }

  .stat-value {
    font-size: 2rem;
  }
}

@media (max-width: 600px) {
  .hero-content {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .hero-title {
    font-size: 1.75rem;
  }

  .stat-value {
    font-size: 1.75rem;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }

  .volunteer-data-table ::v-deep tbody tr td {
    padding: 0.75rem 8px !important;
  }
}

// Card Pattern Decoration
.card-pattern {
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1.5" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="20" cy="80" r="1" fill="rgba(255,255,255,0.1)"/></svg>');
  opacity: 0.6;
  pointer-events: none;
}

// Additional Animations
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.volunteer-stat-card {
  animation: slideInUp 0.6s ease-out;

  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
}

.volunteer-table-card {
  animation: fadeInScale 0.8s ease-out 0.5s both;
}

// Custom Scrollbar
.volunteer-data-table {
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5a6fd8, #6a4190);
  }
}

// Loading States
.loading-shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

// Hover Effects for Better UX
.volunteer-stat-card {
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover::before {
    transform: translateX(100%);
  }
}

// Focus States for Accessibility
.action-btn:focus,
.volunteer-stat-card:focus,
.search-field:focus {
  // outline: 2px solid #667eea;
  outline-offset: 2px;
}

// Print Styles
@media print {
  .volunteer-dashboard {
    background: white !important;
  }

  .hero-header,
  .action-buttons,
  .floating-hearts {
    display: none !important;
  }

  .volunteer-table-card {
    box-shadow: none !important;
    // border: 1px solid #ddd !important;
  }
}

// Dark Mode Support (if needed)
@media (prefers-color-scheme: dark) {
  .volunteer-table-card {
    // background: rgba(45, 55, 72, 0.95);
    color: white;
  }

  .volunteer-data-table ::v-deep tbody tr:hover {
    // background: rgba(102, 126, 234, 0.1) !important;
  }
}

// Additional Utility Classes
.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.bounce {
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
}

// Enhanced Mobile Experience
@media (max-width: 480px) {
  .hero-header {
    padding: 1rem 0;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .volunteer-stat-card {
    margin-bottom: 1rem;
  }

  .stat-icon-wrapper {
    width: 50px;
    height: 50px;
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .table-header {
    padding: 1.5rem 1rem;
  }

  .table-title {
    font-size: 1.5rem;
  }

  .table-subtitle {
    font-size: 1rem;
  }

  .reward-title-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .action-buttons {
    flex-direction: row;
    gap: 0.25rem;
  }

  .action-btn {
    width: 32px !important;
    height: 32px !important;
  }
}

</style>